import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { trackEvent } from '@gjensidige/core-analytics/lib/track-event';
import { TrackingContext } from '@gjensidige/core-analytics/lib/tracking-context';
import { TrackEventProps } from '@gjensidige/core-analytics/lib/types';
import { Close } from '@gjensidige/core-icons/lib/close';
import { KeyboardArrowDown } from '@gjensidige/core-icons/lib/keyboard-arrow-down';

import ClickCollector from '../click-collector';
import { AlertTypeProps } from '../types';
import { DEFAULT_TRACKING } from './tracking';

import styles from '../../less/global/index.module.less';

interface Props {
  title: string;
  text?: string;
  /**
   * Add custom callback for onHide.
   */
  onHideClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

interface DefaultProps extends AlertTypeProps {
  /**
   * Minimizes text to one line and adds expand button.
   * @default false
   */
  expandable?: boolean;
}

interface OptionalProps {
  /** Links/buttons to be added after the text. */
  action?: React.ReactNode;

  /** Adds custom class.   */
  className?: string;
  /** Children can be used if you need content other than just text */
  children?: React.ReactNode;
}

export type ComponentProps = Props &
  DefaultProps &
  OptionalProps &
  TrackEventProps;

export const Global: React.FC<ComponentProps> = ({
  title,
  text,
  alertType = 'information',
  expandable = false,
  enableTracking = true,
  tracking = DEFAULT_TRACKING,
  onHideClick,
  className,
  children,
  action
}) => {
  const { options: trackingOptions } = useContext(TrackingContext);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  const track = (overrides) => {
    if (enableTracking) {
      trackEvent(
        {
          ...DEFAULT_TRACKING,
          ...tracking,
          eventCategory: `global_alert_${alertType}`,
          eventLabel: tracking.eventLabel || title,
          ...overrides
        },
        trackingOptions
      );
    }
  };

  const internalOnHideClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (enableTracking) {
      track({
        eventAction: 'close'
      });
    }
    if (onHideClick) {
      onHideClick(e);
    }
    setIsHidden(true);
  };

  const toggleExpanded = () => {
    track({ eventAction: isExpanded ? 'minimize' : 'expand' });
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    track({ eventAction: 'impression' });
  }, []);

  return (
    !isHidden && (
      <section className={classNames(styles[alertType], className)}>
        <div className={styles.contentContainer}>
          <div className={styles.content} data-testid="global-alert">
            {expandable && (
              <button
                aria-label={isExpanded ? 'minimize' : 'expand'}
                className={styles.expandButton}
                onClick={toggleExpanded}
                data-testid={
                  isExpanded ? 'global-minimize-button' : 'global-expand-button'
                }
              >
                <KeyboardArrowDown
                  aria-label="toggle alert-box content"
                  className={
                    isExpanded ? styles.minimizeIcon : styles.expandIcon
                  }
                />
              </button>
            )}
            <div
              className={classNames(styles.textWrapper, {
                [styles.textMinimized]: expandable && !isExpanded
              })}
            >
              <header className={styles.alertTitle}>{title}</header>
              {text ? (
                <p className={styles.alertText}>{text}</p>
              ) : (
                <div
                  className={styles.alertText}
                  data-testid="global-alert-children"
                >
                  {children}
                </div>
              )}
              {action && (
                <ClickCollector track={track} addClass>
                  {action}
                </ClickCollector>
              )}
            </div>
          </div>
          <button
            aria-label="close alert-box"
            className={styles.hideButton}
            onClick={internalOnHideClick}
            data-testid="global-close-button"
          >
            <Close />
          </button>
        </div>
      </section>
    )
  );
};

export const GlobalAlert = Global;
